import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Grid, IconButton, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import { useRouter } from '../../hooks/useRouter';
import { Context as UsersContext } from '../../api/users/context';
import { Context as AuthContext } from '../../api/auth/context';
import { LinkDetails } from '../../api/users/types';
import { LinkCategoryEnum } from './constant';

function AddToFavoritesButton() {
    const usersContext = useContext(UsersContext);
    const authContext = useContext(AuthContext);

    const [isFavorite, setIsFavorite] = useState(false);
    const { queryObject, location } = useRouter();

    const lastWordInArrayFromPath = useMemo(() => {
        const arrayFromPath = location.pathname.split('/');
        const lastSegment = arrayFromPath[arrayFromPath.length - 1].replace(/-/g, ' ');

        if (!lastSegment.trim()) return 'Dashboard';

        const capitalizedArray = lastSegment
            .split(' ')
            .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ''));
        return capitalizedArray.join(' ');
    }, [location.pathname]);
    const initialPathname = useMemo(
        () => ({ name: lastWordInArrayFromPath, path: location.pathname }),
        [lastWordInArrayFromPath, location.pathname]
    );
    const [currentPathname, setCurrentPathname] = useState<LinkDetails>(initialPathname);

    const getEffectivePathname = useCallback((): LinkDetails => {
        const arrayFromPath = location.pathname.split('/');
        if (location.pathname.includes(LinkCategoryEnum.RESERVATIONS)) {
            const lastSegment = arrayFromPath[arrayFromPath.length - 1];
            if (lastSegment === 'travel-file' && queryObject?.refNumber) {
                return {
                    name: `${lastWordInArrayFromPath} Num. ${queryObject?.refNumber}`,
                    path: `${location.pathname}?ref_number=${queryObject?.refNumber}`
                };
            } else if (lastSegment === 'book-tour' && queryObject?.rooms) {
                return {
                    name: `${lastWordInArrayFromPath} & Rooms: ${queryObject.rooms}`,
                    path: `${location.pathname}?rooms=${queryObject?.rooms}`
                };
            } else if (lastSegment === 'travel-file-list' && queryObject?.tourId) {
                return {
                    name: `${lastWordInArrayFromPath} by Tour`,
                    path: `${location.pathname}?tour_id=${queryObject.tourId}`
                };
            }
        }
        return initialPathname;
    }, [
        initialPathname,
        lastWordInArrayFromPath,
        location.pathname,
        queryObject?.refNumber,
        queryObject.rooms,
        queryObject.tourId
    ]);

    const handleToggle = () => {
        setIsFavorite((prevOpen: boolean) => !prevOpen);
    };
    const title = isFavorite ? 'Remove from Favorites' : 'Add to Favorites';

    // Super user (_id: -1) has no DB row — skip favorites entirely
    const isSuperUser = Number(authContext?.currUser?._id) === -1;

    const currentUser = {
        _id: authContext?.currUser?._id,
        name: authContext?.currUser?.name,
        favoriteLinks: authContext?.currUser?.favoriteLinks
    };
    const allFavoriteLinks = useMemo(() => {
        return (currentUser?.favoriteLinks?.administration || []).concat(
            currentUser?.favoriteLinks?.reservations || []
        );
    }, [currentUser?.favoriteLinks]);

    useEffect(() => {
        const effective = getEffectivePathname();
        setCurrentPathname(effective);
        const isFav = allFavoriteLinks.some((favorite) => favorite.path === effective.path);
        setIsFavorite(isFav);
    }, [location, currentUser.favoriteLinks, getEffectivePathname, allFavoriteLinks]);

    if (isSuperUser) return null;

    let userFavorites = currentUser.favoriteLinks;

    const addToFavorites = () => {
        if (!userFavorites || Array.isArray(userFavorites)) {
            userFavorites = { administration: [], reservations: [] };
        }
        if (!userFavorites.administration) userFavorites.administration = [];
        if (!userFavorites.reservations) userFavorites.reservations = [];
        if (location.pathname.includes(LinkCategoryEnum.ADMINISTRATION)) {
            userFavorites.administration.push(currentPathname);
        } else {
            userFavorites.reservations.push(currentPathname);
        }
        return userFavorites;
    };
    const removeFromFavorites = () => {
        const isAdmin = location.pathname.includes(LinkCategoryEnum.ADMINISTRATION);
        if (isAdmin && userFavorites?.administration) {
            userFavorites.administration = userFavorites.administration.filter(
                (pathname: LinkDetails) => pathname.path !== currentPathname.path
            );
        } else if (userFavorites?.reservations) {
            userFavorites.reservations = userFavorites.reservations.filter(
                (pathname: LinkDetails) => pathname.path !== currentPathname.path
            );
        }
        return userFavorites;
    };

    const handleClick = () => {
        if (isFavorite && !userFavorites) return handleToggle();
        else {
            userFavorites = isFavorite ? removeFromFavorites() : addToFavorites();
            const updatedUser = { _id: authContext?.currUser?._id, favoriteLinks: userFavorites };
            return usersContext
                .put(updatedUser)
                .then(() => {
                    const currUser = authContext?.currUser;
                    if (!currUser) return;
                    authContext.setUser([{ ...currUser, favoriteLinks: userFavorites }]);
                })
                .finally(() => {
                    handleToggle();
                });
        }
    };

    return (
        <Grid>
            <Tooltip
                title={title}
                placement="left"
                enterDelay={100}
                disableInteractive={false}
                leaveDelay={100}
                disableTouchListener={true}
            >
                <IconButton
                    aria-label={`favorite ${title ?? ''}`}
                    sx={{ color: 'accent.main', p: '4px' }}
                    onClick={handleClick}
                    size="small"
                >
                    {isFavorite ? (
                        <StarIcon sx={{ fontSize: 20 }} />
                    ) : (
                        <StarBorderIcon sx={{ fontSize: 20 }} />
                    )}
                </IconButton>
            </Tooltip>
        </Grid>
    );
}

export default AddToFavoritesButton;
